/**
 * AiNamingReportView
 *
 * Full COBOL → Java naming report for the AI Naming node.
 * Shows summary stats + searchable/filterable table of all renames.
 *
 * Data source: GET /ainaming/{jobId}/report
 */

import { useState, useEffect, useCallback } from 'react'
import {
  ArrowLeft, Loader2, AlertCircle, Search, X,
  Tag, Layers, CheckCircle2, TrendingUp, Filter,
} from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'
import './AiNamingReportView.css'

function authHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function ConfBadge({ value }) {
  const pct = Math.round(value * 100)
  const cls = value >= 0.9 ? 'high' : value >= 0.75 ? 'med' : 'low'
  return <span className={`conf-badge conf-${cls}`}>{pct}%</span>
}

export default function AiNamingReportView({ analysisContext, onBack, onClose }) {
  const apiBase = getAPIBaseUrl()
  const handleClose = onBack || onClose

  const jobId =
    analysisContext?.jobs?.ai_naming_job_id ||
    analysisContext?.jobId

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [report, setReport] = useState(null)
  const [search, setSearch] = useState('')
  const [filterConf, setFilterConf] = useState('all') // all | high | med | low
  const [filterService, setFilterService] = useState('all')

  const fetchReport = useCallback(async () => {
    if (!jobId) {
      setError('No AI Naming job ID found.')
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(`${apiBase}/ainaming/${jobId}/report`, { headers: authHeaders() })
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`)
      const data = await res.json()
      setReport(data)
    } catch (e) {
      setError(`Failed to load naming report: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [jobId, apiBase])

  useEffect(() => { fetchReport() }, [fetchReport])

  // Derived data
  const suggestions = report?.suggestions || []
  const summary = report?.summary || {}

  const allServices = [...new Set(suggestions.map(s => s.suggested_service).filter(Boolean))].sort()

  const filtered = suggestions.filter(s => {
    const q = search.toLowerCase()
    const matchSearch = !q ||
      s.paragraph_name?.toLowerCase().includes(q) ||
      s.suggested_method_name?.toLowerCase().includes(q) ||
      s.suggested_service?.toLowerCase().includes(q) ||
      s.business_purpose?.toLowerCase().includes(q)

    const conf = s.confidence || 0
    const matchConf =
      filterConf === 'all' ||
      (filterConf === 'high' && conf >= 0.9) ||
      (filterConf === 'med' && conf >= 0.75 && conf < 0.9) ||
      (filterConf === 'low' && conf < 0.75)

    const matchService = filterService === 'all' || s.suggested_service === filterService

    return matchSearch && matchConf && matchService
  })

  // Sort by confidence desc
  const sorted = [...filtered].sort((a, b) => (b.confidence || 0) - (a.confidence || 0))

  return (
    <div className="ainaming-report-view">
      {/* Header */}
      <div className="ainaming-report-header">
        <button className="back-btn" onClick={handleClose}>
          <ArrowLeft size={16} />
          Back to Workflow
        </button>
        <div className="ainaming-report-title">
          <Tag size={20} />
          <h2>AI Naming Report — COBOL → Java</h2>
        </div>
        {jobId && <span className="ainaming-job-id">Job: {jobId}</span>}
      </div>

      {loading && (
        <div className="ainaming-loading">
          <Loader2 size={32} className="spin" />
          <p>Loading naming report…</p>
        </div>
      )}

      {error && !loading && (
        <div className="ainaming-error">
          <AlertCircle size={20} />
          <p>{error}</p>
          <button onClick={fetchReport}>Retry</button>
        </div>
      )}

      {report && !loading && (
        <>
          {/* Summary Stats */}
          <div className="ainaming-stats-row">
            <div className="ainaming-stat">
              <Tag size={16} />
              <div>
                <span className="stat-value">{summary.suggestions_generated?.toLocaleString() || 0}</span>
                <span className="stat-label">Paragraphs Renamed</span>
              </div>
            </div>
            <div className="ainaming-stat">
              <Layers size={16} />
              <div>
                <span className="stat-value">{summary.services_suggested || 0}</span>
                <span className="stat-label">Services Suggested</span>
              </div>
            </div>
            <div className="ainaming-stat">
              <CheckCircle2 size={16} />
              <div>
                <span className="stat-value">{summary.high_confidence_count || 0}</span>
                <span className="stat-label">High Confidence</span>
              </div>
            </div>
            <div className="ainaming-stat">
              <TrendingUp size={16} />
              <div>
                <span className="stat-value">
                  {summary.avg_confidence != null ? `${Math.round(summary.avg_confidence * 100)}%` : '—'}
                </span>
                <span className="stat-label">Avg Confidence</span>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="ainaming-filters">
            <div className="ainaming-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search COBOL paragraph, Java method, service…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="clear-search" onClick={() => setSearch('')}>
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="ainaming-filter-group">
              <Filter size={13} />
              <select value={filterConf} onChange={e => setFilterConf(e.target.value)}>
                <option value="all">All Confidence</option>
                <option value="high">High (≥90%)</option>
                <option value="med">Medium (75–89%)</option>
                <option value="low">Low (&lt;75%)</option>
              </select>
            </div>

            <div className="ainaming-filter-group">
              <Layers size={13} />
              <select value={filterService} onChange={e => setFilterService(e.target.value)}>
                <option value="all">All Services</option>
                {allServices.map(svc => (
                  <option key={svc} value={svc}>{svc}</option>
                ))}
              </select>
            </div>

            <span className="ainaming-count">{sorted.length} of {suggestions.length} renames</span>
          </div>

          {/* Table */}
          <div className="ainaming-table-wrap">
            <table className="ainaming-table">
              <thead>
                <tr>
                  <th>COBOL Paragraph</th>
                  <th>Java Method</th>
                  <th>Service</th>
                  <th>Business Purpose</th>
                  <th>Conf.</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={5} className="ainaming-empty">No results match your filters.</td>
                  </tr>
                )}
                {sorted.map((row, idx) => (
                  <tr key={idx}>
                    <td className="col-cobol">
                      <code>{row.paragraph_name}</code>
                    </td>
                    <td className="col-java">
                      <code className="java-method">{row.suggested_method_name}</code>
                    </td>
                    <td className="col-service">{row.suggested_service || '—'}</td>
                    <td className="col-purpose">{row.business_purpose || '—'}</td>
                    <td className="col-conf">
                      <ConfBadge value={row.confidence || 0} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
